import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Trophy, Lock, Sparkles } from "lucide-react";

const TIER_STYLES = {
  bronze: { ring: "border-amber-300", bg: "bg-gradient-to-br from-amber-100 to-amber-50", text: "text-amber-700", chip: "bg-amber-100 text-amber-700" },
  silver: { ring: "border-cream-300", bg: "bg-gradient-to-br from-cream-100 to-white", text: "text-plum-700", chip: "bg-cream-100 text-plum-700" },
  gold:   { ring: "border-sun-400",   bg: "bg-gradient-to-br from-sun-100 to-pink-50", text: "text-pink-700", chip: "bg-sun-100 text-sun-700" },
};

/**
 * Section "Mes badges" du tableau de bord habitant.
 * Affiche tous les badges (débloqués + verrouillés) avec barres de progression.
 */
export default function HabitantBadges({ limit = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLocked, setShowLocked] = useState(true);

  useEffect(() => {
    api.get("/badges/mine")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const visible = data.badges
    .filter((b) => showLocked || b.unlocked)
    .slice(0, limit || data.badges.length);
  const ratio = Math.round((data.unlocked_count / data.total_count) * 100);

  return (
    <section className="phenix-card p-4 sm:p-5 bg-white" data-testid="habitant-badges">
      <header className="flex items-start justify-between gap-3 mb-3.5 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sun-400 to-pink-500 text-white grid place-items-center shrink-0 shadow-sm">
            <Trophy size={18} strokeWidth={2.2} />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-base sm:text-lg text-plum-900 leading-tight">Mes badges</h2>
            <p className="text-[12px] text-plum-700/65 mt-0.5">
              <strong className="text-plum-900 font-bold">{data.unlocked_count}/{data.total_count}</strong> débloqués · {ratio}% du chemin
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowLocked((v) => !v)}
          data-testid="badges-toggle-locked"
          className="text-[11px] font-bold text-plum-700 hover:text-pink-500 transition-colors"
        >
          {showLocked ? "Masquer verrouillés" : "Voir verrouillés"}
        </button>
      </header>

      {/* Progress bar global */}
      <div className="mb-4 h-1.5 bg-cream-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-sun-400 via-pink-400 to-plum-600 rounded-full transition-all duration-700"
          style={{ width: `${ratio}%` }}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="badges-grid">
        {visible.map((b) => <BadgeCard key={b.id} b={b} />)}
      </div>

      <p className="mt-3 text-[10px] text-plum-700/50 italic text-center">
        Plus vous participez à la vie d&apos;Istres, plus vous gagnez de badges !
      </p>
    </section>
  );
}

function BadgeCard({ b }) {
  const style = TIER_STYLES[b.tier] || TIER_STYLES.silver;
  const isUnlocked = b.unlocked;
  return (
    <div
      data-testid={`badge-${b.id}`}
      title={b.description}
      className={`relative rounded-2xl p-3 border-2 transition-all ${
        isUnlocked
          ? `${style.ring} ${style.bg} hover:-translate-y-0.5 hover:shadow-md`
          : "border-cream-200 bg-cream-50/60 opacity-70"
      }`}
    >
      <div className="flex flex-col items-center text-center">
        <div className={`w-12 h-12 rounded-full grid place-items-center text-2xl mb-1.5 ${isUnlocked ? "bg-white shadow-sm" : "bg-cream-100 grayscale"}`}>
          {isUnlocked ? b.emoji : <Lock size={16} className="text-plum-700/40" />}
        </div>
        <div className={`font-display font-bold text-[12px] leading-tight ${isUnlocked ? "text-plum-900" : "text-plum-700/55"}`}>
          {b.label}
        </div>
        <div className="text-[9px] text-plum-700/55 mt-0.5 line-clamp-2 leading-tight">{b.criteria}</div>

        {!isUnlocked && b.progress > 0 && (
          <div className="w-full mt-2 h-1 bg-cream-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${style.text.replace("text-", "bg-")} rounded-full`}
              style={{ width: `${Math.round(b.progress * 100)}%`, opacity: 0.7 }}
            />
          </div>
        )}
        {isUnlocked && b.tier === "gold" && (
          <span className="absolute top-1.5 right-1.5">
            <Sparkles size={11} className="text-sun-500" strokeWidth={2.3} />
          </span>
        )}
      </div>
    </div>
  );
}
